// ResourceUploadPage.tsx
// Public resource uploader for images + videos.
// Uses Firebase Storage uploadBytesResumable, which is more reliable for large
// mobile uploads such as Apple iPhone videos.

import {
  FileImageOutlined,
  UploadOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Divider,
  Input,
  message,
  Progress,
  Space,
  Typography,
  Upload,
} from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  type UploadTaskSnapshot,
} from "firebase/storage";

import {
  addDoc,
  collection,
  getFirestore,
  serverTimestamp,
} from "firebase/firestore";

const { Title, Text } = Typography;

type ResourceKind = "image" | "video";

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"];
const VIDEO_EXTENSIONS = ["mp4", "mov", "m4v", "webm", "avi", "mpeg", "mpg"];

const getExtension = (fileName: string) =>
  fileName.split(".").pop()?.toLowerCase() || "";

const getResourceKind = (file: File): ResourceKind | null => {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";

  // iOS/Safari can occasionally provide an empty MIME type, so fall back to ext.
  const ext = getExtension(file.name);
  if (IMAGE_EXTENSIONS.includes(ext)) return "image";
  if (VIDEO_EXTENSIONS.includes(ext)) return "video";

  return null;
};

const sanitizeFileName = (fileName: string) =>
  fileName
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_+/g, "_");

const uploadResumable = (
  storagePath: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<{ fullPath: string; downloadUrl: string | null }> => {
  const storage = getStorage();
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType: file.type || undefined,
    customMetadata: {
      originalName: file.name,
    },
  });

  return new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot: UploadTaskSnapshot) => {
        const percent = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        );
        onProgress?.(percent);
      },
      reject,
      async () => {
        let downloadUrl: string | null = null;

        try {
          downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
        } catch (err) {
          // Public uploads are allowed by storage.rules, but public reads are not.
          // getDownloadURL requires read access, so unauthenticated uploaders can
          // successfully upload while this call is denied. Store the path anyway;
          // admins can resolve it later in ResourceGallery.
          console.warn("Download URL unavailable after upload:", err);
        }

        resolve({
          fullPath: uploadTask.snapshot.ref.fullPath,
          downloadUrl,
        });
      },
    );
  });
};

const generateVideoThumbnail = (file: File, seekTo = 1.5): Promise<File> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Canvas not supported"));
      return;
    }

    const url = URL.createObjectURL(file);
    let settled = false;

    const cleanup = () => {
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      video.load();
    };

    const fail = (reason: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(reason);
    };

    const captureFrame = () => {
      if (!video.videoWidth || !video.videoHeight) {
        fail(new Error("Video dimensions unavailable"));
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size === 0) {
            fail(new Error("Empty thumbnail blob"));
            return;
          }

          if (settled) return;
          settled = true;

          const thumb = new File(
            [blob],
            sanitizeFileName(file.name).replace(/\.[^.]+$/, "") + "_thumb.jpg",
            { type: "image/jpeg" },
          );

          cleanup();
          resolve(thumb);
        },
        "image/jpeg",
        0.85,
      );
    };

    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.crossOrigin = "anonymous";
    video.src = url;

    // Some iPhone videos/codecs may not decode in browser. Do not hang forever.
    const timeout = window.setTimeout(() => {
      fail(new Error("Thumbnail generation timed out"));
    }, 10000);

    video.addEventListener("loadedmetadata", () => {
      const duration = Number.isFinite(video.duration) ? video.duration : seekTo;
      const time = Math.max(0, Math.min(seekTo, duration - 0.1));

      try {
        video.currentTime = time;
      } catch (e) {
        window.clearTimeout(timeout);
        fail(e);
      }
    });

    video.addEventListener("seeked", () => {
      window.clearTimeout(timeout);
      requestAnimationFrame(() => {
        try {
          captureFrame();
        } catch (e) {
          fail(e);
        }
      });
    });

    video.addEventListener("error", () => {
      window.clearTimeout(timeout);
      fail(new Error("Video failed to load or decode"));
    });
  });
};

export default function ResourceUploadPage() {
  const [params] = useSearchParams();

  const firestore = getFirestore();

  const [resourceFile, setResourceFile] = useState<File | null>(null);
  const [resourceKind, setResourceKind] = useState<ResourceKind | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [decodedCollection, setResourceCollection] =
    useState<string>("elements");

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [thumbnailWarning, setThumbnailWarning] = useState<string | null>(null);

  useEffect(() => {
    setResourceCollection(params.get("collection") || "General");
  }, [params]);

  const handleFileSelected = async (file: File) => {
    const kind = getResourceKind(file);

    if (!kind) {
      message.error("Please select a supported image or video file.");
      return Upload.LIST_IGNORE;
    }

    setResourceFile(file);
    setResourceKind(kind);
    setThumbnailWarning(null);

    if (!title) {
      setTitle(file.name.replace(/\.[^.]+$/, ""));
    }

    if (kind === "image") {
      // For images, the image itself can be used as the gallery thumbnail.
      setThumbnailFile(file);
      return false;
    }

    // Videos get a generated thumbnail when the browser can decode the video.
    // iPhone HEVC/MOV thumbnails can fail in-browser, but upload should continue.
    try {
      const generatedThumb = await generateVideoThumbnail(file);
      setThumbnailFile(generatedThumb);
    } catch (err) {
      console.warn("Thumbnail generation failed:", err);
      setThumbnailFile(null);
      setThumbnailWarning(
        "Thumbnail preview could not be generated on this device, but the video can still be uploaded.",
      );
    }

    return false;
  };

  const uploadResource = async () => {
    try {
      if (!resourceFile || !resourceKind) {
        message.error("Please select an image or video file.");
        return;
      }

      setUploading(true);
      setUploadProgress(0);

      const timestamp = Date.now();
      const safeName = sanitizeFileName(resourceFile.name);
      const baseFolder = resourceKind === "image" ? "images" : "videos";

      const resourcePath = `resources/${decodedCollection}/${baseFolder}/${timestamp}_${safeName}`;

      const resourceUpload = await uploadResumable(
        resourcePath,
        resourceFile,
        (percent) => setUploadProgress(percent),
      );

      let thumbnailUpload: { fullPath: string; downloadUrl: string | null } | null =
        null;

      if (thumbnailFile) {
        if (resourceKind === "image" && thumbnailFile === resourceFile) {
          // No duplicate upload required for image thumbnails.
          thumbnailUpload = resourceUpload;
        } else {
          const thumbPath = `resources/${decodedCollection}/thumbnails/${timestamp}_${sanitizeFileName(
            thumbnailFile.name,
          )}`;
          thumbnailUpload = await uploadResumable(thumbPath, thumbnailFile);
        }
      }

      await addDoc(collection(firestore, "resources"), {
        title,
        description,
        resourceCollection: decodedCollection,
        resourceType: resourceKind,
        contentType: resourceFile.type || null,

        // Backwards-compatible names used by ResourceGalleryPage.
        videoPathUrl: resourceUpload.fullPath,
        videoDownloadUrl: resourceUpload.downloadUrl,
        videoFileName: resourceFile.name,

        thumbnailPathUrl: thumbnailUpload?.fullPath || null,
        thumbnailDownloadUrl: thumbnailUpload?.downloadUrl || null,
        thumbnailFileName: thumbnailFile?.name || null,

        createdAt: serverTimestamp(),
      });

      message.success("Resource uploaded successfully.");

      setResourceFile(null);
      setResourceKind(null);
      setThumbnailFile(null);
      setTitle("");
      setDescription("");
      setUploadProgress(0);
      setThumbnailWarning(null);
    } catch (error) {
      console.error(error);
      message.error("Upload failed. Please check your connection and try again.");
    } finally {
      setUploading(false);
    }
  };

  const thumbnailUrl = useMemo(() => {
    if (!thumbnailFile) return null;
    return URL.createObjectURL(thumbnailFile);
  }, [thumbnailFile]);

  useEffect(() => {
    return () => {
      if (thumbnailUrl) URL.revokeObjectURL(thumbnailUrl);
    };
  }, [thumbnailUrl]);

  return (
    <div className="flex justify-center items-center min-h-screen bg-[#1f1e23] w-full p-4">
      <div
        style={{
          maxWidth: 700,
          width: "100%",
          margin: "40px auto",
        }}
      >
        <Card>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <Title level={2}>Resource Upload</Title>

              <Text>
                Uploading to collection: <strong>{decodedCollection}</strong>
              </Text>
            </div>

            <Divider />

            <Input
              placeholder="Resource Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <Input.TextArea
              placeholder="Description"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <div className="mt-4 gap-2 flex flex-col">
              <Text strong>Image or Video File</Text>

              <Upload
                accept="image/*,video/*,.heic,.heif,.mov,.mp4,.m4v,.webm"
                beforeUpload={handleFileSelected}
                maxCount={1}
                fileList={
                  resourceFile
                    ? [
                        {
                          uid: "resource",
                          name: resourceFile.name,
                          status: "done",
                        } as UploadFile,
                      ]
                    : []
                }
                onRemove={() => {
                  setResourceFile(null);
                  setResourceKind(null);
                  setThumbnailFile(null);
                  setThumbnailWarning(null);
                }}
              >
                <Button
                  icon={
                    resourceKind === "image" ? (
                      <FileImageOutlined />
                    ) : (
                      <VideoCameraOutlined />
                    )
                  }
                >
                  Select Image or Video
                </Button>
              </Upload>

              {resourceFile && (
                <Text type="secondary">
                  Selected {resourceKind}: {resourceFile.name}
                </Text>
              )}
            </div>

            <div className="mt-4 gap-2 flex flex-col items-center">
              {thumbnailUrl && resourceKind === "image" && (
                <img
                  src={thumbnailUrl}
                  alt="Selected resource preview"
                  width={220}
                  style={{ maxHeight: 220, objectFit: "cover" }}
                />
              )}

              {thumbnailUrl && resourceKind === "video" && (
                <img
                  src={thumbnailUrl}
                  alt="Generated video thumbnail preview"
                  width={220}
                  style={{ aspectRatio: "16 / 9", objectFit: "cover" }}
                />
              )}

              {thumbnailWarning && (
                <Text type="warning" style={{ textAlign: "center" }}>
                  {thumbnailWarning}
                </Text>
              )}
            </div>

            {uploading && <Progress percent={uploadProgress} />}

            <Button
              type="primary"
              icon={<UploadOutlined />}
              loading={uploading}
              onClick={uploadResource}
              block
            >
              Upload Resource
            </Button>
          </Space>
        </Card>
      </div>
    </div>
  );
}
